import { ApiService } from '../services/api'
import { AppState } from '../stores/app-state'
import { AuthManager } from './auth-manager'
import { UIManager } from './ui-manager'
import { TextProcessingEventManager } from './text-processing-event-manager'
import { DataManager } from './data-manager'
import { DocumentManager } from './document-manager'
import { SelectionManager } from './selection-manager'
import { ProcessingManager } from './processing-manager'
import { ModalManager } from './modal-manager'
import { OCRRenderer } from './ocr-renderer'
import { EventManager } from './event-manager'
import { Toast } from '../components/toast'
import { WorkflowNav } from '../components/workflow-nav'
import { Modal } from '../components/modal'
import { logger } from '../utils/logger'

export class AppInitializer {
  private apiService: ApiService
  private appState: AppState
  private authManager: AuthManager
  private uiManager: UIManager
  private textProcessingEventManager: TextProcessingEventManager
  private dataManager: DataManager
  private documentManager: DocumentManager
  private selectionManager: SelectionManager
  private processingManager: ProcessingManager
  private modalManager: ModalManager
  private ocrRenderer: OCRRenderer
  private eventManager: EventManager
  private toast: Toast
  private workflowNav: WorkflowNav | null = null
  private appInstance: any = null

  constructor() {
    this.apiService = new ApiService()
    this.appState = new AppState()
    this.authManager = new AuthManager()
    this.uiManager = new UIManager()
    this.textProcessingEventManager = new TextProcessingEventManager()
    this.dataManager = new DataManager(this.apiService, this.appState)
    this.documentManager = new DocumentManager(this.apiService, this.appState)
    this.selectionManager = new SelectionManager(this.apiService, this.appState)
    this.toast = new Toast()
    this.processingManager = new ProcessingManager(this.apiService, this.appState, this.uiManager, this.toast)
    this.modalManager = new ModalManager(this.apiService, this.appState, this.documentManager, this.dataManager, this.selectionManager, this.toast)
    this.ocrRenderer = new OCRRenderer(this.apiService, this.appState, this.processingManager, this.uiManager, this.toast)
    this.eventManager = new EventManager(this.apiService, this.appState, this.documentManager, this.dataManager, this.selectionManager, this.processingManager, this.modalManager, this.ocrRenderer, this.uiManager, this.toast)
  }

  public getManagers() {
    return {
      apiService: this.apiService,
      appState: this.appState,
      authManager: this.authManager,
      uiManager: this.uiManager,
      textProcessingEventManager: this.textProcessingEventManager,
      dataManager: this.dataManager,
      documentManager: this.documentManager,
      selectionManager: this.selectionManager,
      processingManager: this.processingManager,
      modalManager: this.modalManager,
      ocrRenderer: this.ocrRenderer,
      eventManager: this.eventManager,
      toast: this.toast,
      workflowNav: this.workflowNav
    }
  }

  public setAppInstance(appInstance: any): void {
    this.appInstance = appInstance
    // Make app instance available globally for debugging and access
    ;(window as any).appInstance = appInstance
  }

  public setupGlobalUtilities(): void {
    // Make Modal class available globally
    ;(window as any).Modal = Modal

    // Expose managers globally for OCR and other features
    ;(window as any).processingManager = this.processingManager
    ;(window as any).apiService = this.apiService
    ;(window as any).appState = this.appState

    // Make debugging utilities available globally
    ;(window as any).debugApp = {
      testGenerateList: () => this.processingManager.generateNumberedList(),
      testConvertCoords: () => this.processingManager.convertListToCoordinates(),
      getAppState: () => this.appState,
      getSelections: () => this.appState.getSelections(),
      getCoordinates: () => this.appState.getCoordinates(),
      getDescriptionList: () => this.appState.getDescriptionList(),
      testEdit: () => {
        logger.info('Edit functionality is now in the left panel of the plot!')
        logger.info('Look for orange "✏️ Edit" buttons next to each coordinate')
      },
      logState: () => {
        logger.debug('App State Debug:', {
          selections: this.appState.getSelections().length,
          coordinates: this.appState.getCoordinates().length,
          descriptionList: this.appState.getDescriptionList()?.length || 0,
          currentProject: this.appState.getCurrentProject()?.name || 'None'
        })
      }
    }
    
    // Make image modal function available globally
    ;(window as any).showImageModal = this.uiManager.showImageModal.bind(this.uiManager)
  }

  public setupEventListeners(): void {
    // Listen for step content re-rendering requests (set up early)
    window.addEventListener('render-step-content', () => {
      console.log('🎯 render-step-content event received')
      if (this.appInstance && this.appInstance.renderStepContent) {
        console.log('🎯 Calling appInstance.renderStepContent()')
        this.appInstance.renderStepContent()
      } else {
        console.error('❌ appInstance or renderStepContent method not available')
      }
    })
    
    // Listen for authentication success to initialize main app
    window.addEventListener('auth-success', (event: any) => {
      const container = event.detail.container
      this.initializeUI(container)
      this.eventManager.setWorkflowNav(this.workflowNav!)
      
      console.log('🎯 Setting app instance in EventManager:', {
        appInstance: !!this.appInstance,
        hasRenderSelectionResults: !!(this.appInstance && this.appInstance.renderSelectionResults)
      })
      this.eventManager.setApp(this.appInstance)
      this.eventManager.setupAllEventListeners()
      
      // Health monitoring is now handled by individual managers as needed
    })
  }

  private initializeUI(container: HTMLElement): void {
    container.innerHTML = `
      <div class="min-h-screen bg-gray-50 flex flex-col">
        <div id="workflow-nav"></div>
        <main class="flex-1 flex items-start justify-center px-4 pt-4 pb-8 main-content">
          <div id="step-content" class="w-full max-w-7xl mx-auto"></div>
        </main>
        <div id="toast-container" class="fixed right-4 space-y-2" style="top: 100px; z-index: 110;"></div>
      </div>
    `

    this.workflowNav = new WorkflowNav(
      document.getElementById('workflow-nav')!,
      this.appState,
      this.apiService,
      this.toast
    )
    
    this.toast.init(document.getElementById('toast-container')!)
    console.log('📊 Workflow navigation initialized:', !!this.workflowNav)
    
    // Trigger initial step content rendering
    console.log('🎯 Dispatching initial render-step-content event')
    window.dispatchEvent(new CustomEvent('render-step-content'))
  }

  public async init(): Promise<void> {
    try {
      logger.appInit('Initializing Doc2Plot Frontend v2.0.0')
      
      const appContainer = document.getElementById('app')
      if (!appContainer) {
        throw new Error('App container not found')
      }

      // Check if authentication is required
      await this.authManager.checkAuthentication(appContainer)
      
      logger.appSuccess('Doc2Plot Frontend initialized successfully')
    } catch (error) {
      logger.appError('Failed to initialize app:', error)
      this.toast.error('Failed to initialize application')
    }
  }
}
